import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Send, UserPlus, Phone, MapPin, Calendar,
  TrendingUp, TrendingDown, Minus, Users, FileText, Plane, Wallet,
  AlertCircle, Clock, FileCheck, Receipt, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import TargetsWidget from '@/components/dashboard/TargetsWidget';
import CollectionsWidget from '@/components/dashboard/CollectionsWidget';
import PaceStrip from '@/components/dashboard/PaceStrip';
import QuickActions from '@/components/dashboard/QuickActions';
import SmartAssist from '@/components/dashboard/SmartAssist';
import ReconWatchChip from '@/components/dashboard/ReconWatchChip';
import TallyExportReminder from '@/components/dashboard/TallyExportReminder';
import ClientMoments from '@/components/dashboard/ClientMoments';
import VisaPendingWidget from '@/components/dashboard/VisaPendingWidget';
import { tripPaymentStatus, PAYMENT_BADGE } from '@/utils/paymentStatus';
import { WeatherIcon, weatherLabel, extractWeatherCities, getCityWeather } from '@/utils/weather';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ----------------------------------------------------------------------------
// Helpers (pure, no mocks — every value derives from fetched backend data)
// ----------------------------------------------------------------------------

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const parseDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const daysBetween = (a, b) => Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));

const relativeTime = (raw) => {
  if (!raw) return '';
  // Timestamps are stored UTC in Mongo but often serialised WITHOUT a tz
  // suffix (e.g. "2026-02-15T10:30:00"). `new Date()` reads those as LOCAL
  // time → off by the browser offset (IST = +5:30). Append 'Z' to any naive
  // datetime so it parses as UTC, then render relative to the browser tz.
  let s = raw;
  if (typeof s === 'string' && s.includes('T') && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const startOfDay = (d = new Date()) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d = new Date()) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

// Dashboard-only: hide obvious test/QA records (status=test, source=pytest, or
// a clear test-marker name) so the live dashboard stays clean. Does not delete
// any data — purely a display filter.
const isTestRecord = (x) => {
  if (!x) return false;
  const status = String(x.status || '').toLowerCase();
  const source = String(x.source || '').toLowerCase();
  const name = String(x.guestName || x.name || x.fullName || '').toLowerCase();
  if (status === 'test') return true;
  if (source === 'pytest' || source === 'test') return true;
  return /^(test[_\s]|qa[_\s])/.test(name) || name.includes('pytest');
};

// ----------------------------------------------------------------------------
// Subcomponents
// ----------------------------------------------------------------------------

const MetricCard = ({ title, value, delta, deltaPct, trend, tone = 'slate', icon: Icon, href, testId }) => {
  const toneMap = {
    slate: 'text-slate-700 bg-slate-50',
    teal:  'text-teal-700 bg-teal-50',
    amber: 'text-amber-700 bg-amber-50',
    red:   'text-red-700 bg-red-50',
    emerald: 'text-emerald-700 bg-emerald-50',
  };
  // Trend arrow renders only when `trend` (an integer % change) is set.
  // Positive = green-up; negative = red-down; zero = flat-gray.
  const trendUp = typeof trend === 'number' && trend > 0;
  const trendDown = typeof trend === 'number' && trend < 0;
  const trendColor = trendUp ? 'text-emerald-600' : trendDown ? 'text-rose-600' : 'text-slate-400';
  const TrendArrow = trendUp ? TrendingUp : trendDown ? TrendingDown : Minus;

  const content = (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-[10px] px-4 py-3 shadow-sm hover:shadow-md transition-shadow h-full"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{title}</p>
          <p className="text-[24px] font-heading font-bold text-slate-900 mt-1 truncate leading-none">{value}</p>
          {typeof trend === 'number' ? (
            <div className={`flex items-center gap-1 mt-1 text-[11px] font-mono ${trendColor}`}>
              <TrendArrow className="h-3 w-3" />
              {trendUp ? '+' : ''}{trend}%
              {deltaPct ? <span className="text-slate-400 ml-1">{deltaPct}</span> : null}
            </div>
          ) : delta ? <p className="text-[11px] text-slate-500 mt-1">{delta}</p> : null}
        </div>
        {Icon && (
          <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </div>
  );
  return href ? <Link to={href} className="block h-full">{content}</Link> : content;
};

const StatusPill = ({ status }) => {
  const map = {
    Overdue:  'bg-red-50 text-red-700 border-red-200',
    Today:    'bg-amber-50 text-amber-700 border-amber-200',
    Tomorrow: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Upcoming: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border uppercase tracking-wide ${map[status] || map.Upcoming}`}>
      {status}
    </span>
  );
};

// Compact live weather chip for a departure's destination (keyless Open-Meteo,
// module-cached so repeated destinations hit the network once). Renders
// nothing until/unless current conditions resolve.
const DepartureWeather = ({ destination, leadingSep }) => {
  const [wx, setWx] = useState(null);
  const cityKey = extractWeatherCities(destination, []).join('|');
  useEffect(() => {
    let alive = true;
    const first = cityKey ? cityKey.split('|')[0] : '';
    if (!first) return undefined;
    getCityWeather(first).then((r) => { if (alive) setWx(r); });
    return () => { alive = false; };
  }, [cityKey]);
  if (!wx || !wx.current || wx.current.temperature_2m == null) return null;
  return (
    <>
      {leadingSep && <span className="text-slate-300">•</span>}
      <span
        className="inline-flex items-center gap-1 text-slate-500"
        data-testid="departure-weather"
        title={`${wx.city}: ${weatherLabel(wx.current.weather_code)}`}
      >
        <WeatherIcon code={wx.current.weather_code} className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.75} />
        {Math.round(wx.current.temperature_2m)}°
      </span>
    </>
  );
};

const SectionCard = ({ title, action, testId, children, emptyHint, isEmpty }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-[10px] shadow-sm flex flex-col h-full">
    <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
    <div className="flex-1 p-3 overflow-hidden">
      {isEmpty ? (
        <div className="flex items-center justify-center text-center text-sm text-slate-500 py-10 px-4">
          {emptyHint}
        </div>
      ) : children}
    </div>
  </div>
);

// Top destinations — compact bars version designed to sit at 40% width
// next to the Targets card. Max 4 rows, 18px bar height per spec.
// Count = number of trips DEPARTING in the selected period per destination.
const DEST_PERIODS = [
  { v: 'month', label: 'This Month' },
  { v: 'quarter', label: 'This Quarter' },
  { v: 'year', label: 'This Year' },
  { v: 'all', label: 'All Time' },
];
const DEST_PERIOD_HINT = {
  month: 'No trips departing this month',
  quarter: 'No trips departing this quarter',
  year: 'No trips departing this year',
  all: 'No confirmed trips yet',
};
const TopDestinationsCard = ({ items = [], period = 'month', onPeriodChange }) => {
  const selector = (
    <select
      value={period}
      onChange={(e) => onPeriodChange?.(e.target.value)}
      data-testid="top-dest-period-select"
      className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 text-slate-600 bg-white focus:outline-none focus:ring-1 focus:ring-teal-400"
    >
      {DEST_PERIODS.map((p) => (
        <option key={p.v} value={p.v}>{p.label}</option>
      ))}
    </select>
  );
  if (!items.length) {
    return (
      <SectionCard
        testId="card-top-destinations"
        title="Top Destinations"
        action={selector}
        isEmpty
        emptyHint={DEST_PERIOD_HINT[period] || DEST_PERIOD_HINT.all}
      />
    );
  }
  const trimmed = items.slice(0, 4);
  const max = trimmed[0]?.trips || 1;
  const colors = ['bg-emerald-500', 'bg-emerald-400', 'bg-emerald-300', 'bg-emerald-200'];
  return (
    <SectionCard
      testId="card-top-destinations"
      title="Top Destinations"
      action={selector}
    >
      <ul className="space-y-1.5" data-testid="top-destinations-list">
        {trimmed.map((d, idx) => {
          const pct = Math.max(10, Math.round((d.trips / max) * 100));
          return (
            <li key={d.destination} data-testid={`top-dest-${idx}`} className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-400 w-4">{idx + 1}</span>
              <span className="text-xs font-medium text-slate-800 flex-1 truncate">{d.destination}</span>
              <div className="flex-1 max-w-[110px] bg-slate-100 rounded-full overflow-hidden" style={{ height: 18 }}>
                <div className={`h-full rounded-full ${colors[idx] || 'bg-emerald-300'}`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[11px] text-slate-500 w-16 text-right whitespace-nowrap">{d.trips} trip{d.trips === 1 ? '' : 's'}</span>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
};

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    trips: [],
    websiteLeads: [],
    paymentLinks: [],
    invoices: [],
    vouchers: [],
    crmFollowups: [],
    accounting: null,
    adminDashboard: null,
  });
  // Phase 16 — additional Dashboard+ payload (pace strip, alerts,
  // moments, lead health, smart-assist chips). One extra GET so first
  // paint stays fast.
  const [plus, setPlus] = useState({ pace: {}, moments: [], leadHealth: {}, assist: [] });

  // Standalone refetch so the SmartAssist chip-dismiss action can refresh
  // just the `assist` slice (and incidentally the other plus fields) without
  // re-pulling the heavy trips/invoices arrays. Cheap — single GET.
  const refetchPlus = React.useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/dashboard/plus`);
      setPlus(r.data || { pace: {}, moments: [], leadHealth: {}, assist: [] });
    } catch (e) {
      /* silent — chip just won't disappear; the toast in SmartAssist already covered the dismiss outcome */
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const safe = (url, fallback = []) =>
          axios.get(url).then((r) => r.data).catch(() => fallback);

        const [trips, leadsRes, linksRes, invoices, vouchers, accounting, adminDash, plusRes, crmFollowups, leadStatsRes] = await Promise.all([
          safe(`${BACKEND_URL}/api/trips`),
          safe(`${BACKEND_URL}/api/website-leads?limit=100`, { items: [] }),
          safe(`${BACKEND_URL}/api/payments/links?limit=100`, { items: [] }),
          safe(`${BACKEND_URL}/api/invoices`),
          safe(`${BACKEND_URL}/api/vouchers`),
          safe(`${BACKEND_URL}/api/accounting/reports/summary`, null),
          safe(`${BACKEND_URL}/api/admin/dashboard`, null),
          safe(`${BACKEND_URL}/api/dashboard/plus`, { pace: {}, moments: [], leadHealth: {}, assist: [] }),
          safe(`${BACKEND_URL}/api/followups`),
          safe(`${BACKEND_URL}/api/website-leads/stats`, { open: 0, total: 0 }),
        ]);

        setData({
          trips: (Array.isArray(trips) ? trips : []).filter((t) => !isTestRecord(t)),
          websiteLeads: (Array.isArray(leadsRes?.items) ? leadsRes.items : (Array.isArray(leadsRes) ? leadsRes : [])).filter((l) => !isTestRecord(l)),
          paymentLinks: Array.isArray(linksRes?.items) ? linksRes.items : (Array.isArray(linksRes) ? linksRes : []),
          invoices: Array.isArray(invoices) ? invoices : [],
          vouchers: Array.isArray(vouchers) ? vouchers : [],
          crmFollowups: Array.isArray(crmFollowups) ? crmFollowups : [],
          accounting,
          adminDashboard: adminDash,
          leadStats: leadStatsRes || { open: 0, total: 0 },
        });
        setPlus(plusRes || { pace: {}, moments: [], leadHealth: {}, assist: [] });
      } catch (e) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ---- DERIVED ---------------------------------------------------------------
  const today = startOfDay();
  const endToday = endOfDay();
  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0); endOfMonth.setHours(23, 59, 59, 999);
  const in7Days = new Date(today); in7Days.setDate(in7Days.getDate() + 7);
  const in30Days = new Date(today); in30Days.setDate(in30Days.getDate() + 30);

  // Tabs — Departures (Today / This Week / This Month) and Follow-ups
  // (Today / This Week / This Month). Default to "week" because that's
  // the most actionable horizon for an agent's daily standup.
  const [departureTab, setDepartureTab] = useState('week');
  const [followUpTab, setFollowUpTab] = useState('today');
  // Task 2 — Top Destinations period filter (departures). Default = This Month.
  const [destPeriod, setDestPeriod] = useState('month');
  // Phase 17.2 #9 — Recent Activity tabs (all | leads | trips). Mirrors
  // the chip-tab pattern used by Upcoming Departures.
  const [activityTab, setActivityTab] = useState('all');

  // Follow-ups: combine
  //  • leads with a scheduled nextFollowUpDate (primary source — explicit)
  //  • trips with travelStartDate within 7 days that are still in draft/sent
  //    (legacy proxy; keeps the card useful before any follow-up is logged)
  const followUps = useMemo(() => {
    const list = [];
    // Entities already represented by an explicit CRM follow-up — used to
    // de-dupe the legacy lead/trip proxies below so counts stay accurate.
    const covered = new Set();

    // (0) Explicit CRM follow-ups (followups collection) — the canonical
    // follow-up system. Created from a trip OR lead detail screen anywhere in
    // the CRM. Pending only; due within the next 7 days (or overdue).
    for (const fu of data.crmFollowups) {
      if ((fu.outcome || 'pending') !== 'pending') continue;
      const due = parseDate(fu.scheduled_date);
      if (!due) continue;
      const diff = daysBetween(startOfDay(due), today);
      let label;
      if (diff < 0) label = 'Overdue';
      else if (diff === 0) label = 'Today';
      else if (diff === 1) label = 'Tomorrow';
      else if (diff <= 7) label = 'Upcoming';
      else continue;
      const isTrip = !!fu.trip_id;
      covered.add(isTrip ? `trip:${fu.trip_obj_id || fu.trip_id}` : `lead:${fu.lead_id}`);
      list.push({
        kind: isTrip ? 'trip' : 'lead',
        id: fu.id,
        title: fu.entity_name || 'Follow-up',
        sub: fu.entity_sub || fu.notes || '',
        phone: fu.entity_phone || '',
        nextDate: due,
        callCount: 0,
        href: fu.href || (isTrip ? (fu.trip_obj_id ? `/quotes/${fu.trip_obj_id}/edit` : '/quotes') : '/leads'),
        label,
        daysDiff: diff,
      });
    }

    for (const l of data.websiteLeads) {
      if (!l.nextFollowUpDate) continue;
      if (['converted', 'lost'].includes((l.status || '').toLowerCase())) continue;
      if (covered.has(`lead:${l.leadId}`)) continue;
      const due = parseDate(l.nextFollowUpDate);
      if (!due) continue;
      const diff = daysBetween(startOfDay(due), today);
      let label;
      if (diff < 0) label = 'Overdue';
      else if (diff === 0) label = 'Today';
      else if (diff === 1) label = 'Tomorrow';
      else if (diff <= 7) label = 'Upcoming';
      else continue;
      list.push({
        kind: 'lead',
        id: l.leadId,
        title: l.name || l.email || 'Lead',
        sub: l.destination || l.email || '',
        phone: l.phone,
        nextDate: due,
        callCount: l.callCount || 0,
        href: '/website?tab=leads',
        label,
        daysDiff: diff,
      });
    }

    for (const t of data.trips) {
      const startDate = parseDate(t.travelStartDate);
      if (!startDate) continue;
      if (!['draft', 'sent'].includes((t.status || '').toLowerCase())) continue;
      if (covered.has(`trip:${t.id}`)) continue;
      const diff = daysBetween(startOfDay(startDate), today);
      let label;
      if (diff < 0) label = 'Overdue';
      else if (diff === 0) label = 'Today';
      else if (diff === 1) label = 'Tomorrow';
      else if (diff <= 7) label = 'Upcoming';
      else continue;
      list.push({
        kind: 'trip',
        id: t.id,
        title: t.guestName || '(Unnamed)',
        sub: t.destination || '—',
        phone: t.phone,
        nextDate: startDate,
        callCount: 0,
        href: `/quotes/${t.id}/edit`,
        label,
        daysDiff: diff,
      });
    }

    return list.sort((a, b) => a.daysDiff - b.daysDiff).slice(0, 8);
  }, [data.trips, data.websiteLeads, data.crmFollowups, today]);

  // Upcoming departures: ALL confirmed trips with travelStartDate ahead of
  // today, categorised so we can render filtered tabs (today / week / month).
  // Visa-pending = international trip without a visa doc; voucher-missing =
  // no voucher doc references this tripId.
  const upcomingAll = useMemo(() => {
    const voucherTripIds = new Set(data.vouchers.map((v) => v.tripId).filter(Boolean));
    return data.trips
      .map((t) => {
        const d = parseDate(t.travelStartDate);
        return d ? { t, d } : null;
      })
      .filter(Boolean)
      .filter(({ t, d }) => {
        const status = (t.status || '').toLowerCase();
        const isConfirmed = ['confirmed', 'accepted', 'completed'].includes(status);
        return isConfirmed && d >= today && d <= in30Days;
      })
      .sort((a, b) => a.d - b.d)
      .map(({ t, d }) => {
        const isInternational = (t.locationType || '').toLowerCase() === 'international';
        const visaStatus = (t.visaStatus || '').toLowerCase();
        return {
          trip: t,
          startDate: d,
          pax: (t.adults || 0) + (t.children || 0) + (t.infants || 0),
          vouchersMissing: !voucherTripIds.has(t.tripId),
          visaPending: isInternational && (!visaStatus || /pending|missing|requested/.test(visaStatus)),
        };
      });
  }, [data.trips, data.vouchers, today, in30Days]);

  // Ongoing today = trips with travelStartDate ≤ today ≤ travelEndDate.
  const ongoingToday = useMemo(() => {
    const voucherTripIds = new Set(data.vouchers.map((v) => v.tripId).filter(Boolean));
    return data.trips
      .map((t) => {
        const sd = parseDate(t.travelStartDate);
        const ed = parseDate(t.travelEndDate) || sd;
        return { t, sd, ed };
      })
      .filter(({ t, sd, ed }) => {
        const status = (t.status || '').toLowerCase();
        const isConfirmed = ['confirmed', 'accepted', 'completed'].includes(status);
        return isConfirmed && sd && ed && sd <= endToday && ed >= today;
      })
      .map(({ t, sd, ed }) => ({
        trip: t,
        startDate: sd,
        endDate: ed,
        pax: (t.adults || 0) + (t.children || 0) + (t.infants || 0),
        vouchersMissing: !voucherTripIds.has(t.tripId),
      }));
  }, [data.trips, data.vouchers, today, endToday]);

  // Departures filtered by selected tab.
  const upcoming = useMemo(() => {
    if (departureTab === 'today') return upcomingAll.filter((u) => u.startDate <= endToday).slice(0, 8);
    if (departureTab === 'week') return upcomingAll.filter((u) => u.startDate <= in7Days).slice(0, 8);
    return upcomingAll.slice(0, 8); // month
  }, [upcomingAll, departureTab, endToday, in7Days]);

  // Top destinations by trips DEPARTING in the selected period (Task 2).
  // Period bounds derived from `destPeriod`; default = This Month. Count = the
  // number of confirmed/accepted/completed trips whose travelStartDate falls in
  // the window, grouped by destination, sorted by trip count.
  const destPeriodBounds = useMemo(() => {
    const y = today.getFullYear();
    const m = today.getMonth();
    if (destPeriod === 'month') {
      return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0, 23, 59, 59, 999) };
    }
    if (destPeriod === 'quarter') {
      const qStart = Math.floor(m / 3) * 3;
      return { start: new Date(y, qStart, 1), end: new Date(y, qStart + 3, 0, 23, 59, 59, 999) };
    }
    if (destPeriod === 'year') {
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    }
    return { start: null, end: null }; // all time
  }, [destPeriod, today]);

  const topDestinations = useMemo(() => {
    const { start, end } = destPeriodBounds;
    const counts = {};
    for (const t of data.trips) {
      if (!['confirmed', 'accepted', 'completed'].includes((t.status || '').toLowerCase())) continue;
      const d = parseDate(t.travelStartDate);
      if (!d) continue;
      if (start && d < start) continue;
      if (end && d > end) continue;
      const dest = t.destination || 'Unknown';
      if (!counts[dest]) counts[dest] = { destination: dest, trips: 0, pax: 0 };
      counts[dest].trips += 1;
      counts[dest].pax += (t.adults || 0) + (t.children || 0) + (t.infants || 0);
    }
    return Object.values(counts).sort((a, b) => b.trips - a.trips || b.pax - a.pax).slice(0, 5);
  }, [data.trips, destPeriodBounds]);

  // Recent activity — union of last trips/leads/payments/invoices.
  // Phase 17.2 #9 — keep the full list of recent events (not just the
  // top 5) so the per-tab filter (`all`/`leads`/`trips`) has enough
  // history to slice from. The Render layer still caps each tab to 5.
  const activityRaw = useMemo(() => {
    const evts = [];
    for (const t of data.trips) evts.push({ kind: 'trip', when: t.createdAt, text: `New trip ${t.displayTripId || t.tripId || ''} — ${t.guestName || 'guest'} → ${t.destination || '?'}`, href: `/quotes/${t.id}/edit`, icon: FileText });
    for (const l of data.websiteLeads) evts.push({ kind: 'lead', when: l.createdAt, text: `New lead: ${l.name || l.email || l.phone || 'anonymous'}${l.destination ? ' — ' + l.destination : ''}`, href: '/website', icon: UserPlus });
    for (const p of data.paymentLinks) if ((p.status || '').toLowerCase() === 'paid') evts.push({ kind: 'pay', when: p.paidAt || p.updatedAt || p.createdAt, text: `Payment received ${INR(p.amount)} from ${p.customerName || 'customer'}`, href: '/payment-links', icon: Wallet });
    for (const inv of data.invoices) evts.push({ kind: 'inv', when: inv.createdAt, text: `Invoice ${inv.invoiceNumber} issued to ${(inv.client && inv.client.name) || '—'}`, href: '/invoices', icon: Receipt });
    return evts
      .filter((e) => e.when)
      .sort((a, b) => new Date(b.when) - new Date(a.when));
  }, [data]);

  const activity = useMemo(() => {
    if (activityTab === 'leads') return activityRaw.filter((e) => e.kind === 'lead').slice(0, 5);
    if (activityTab === 'trips') return activityRaw.filter((e) => e.kind === 'trip').slice(0, 5);
    return activityRaw.slice(0, 5);
  }, [activityRaw, activityTab]);

  // Pipeline stages — derived from trips + leads
  const pipeline = useMemo(() => {
    const newLeads = data.websiteLeads.filter((l) => (l.status || 'new') === 'new').length;
    const contacted = data.websiteLeads.filter((l) => ['contacted', 'qualified'].includes(l.status)).length;
    const sent = data.trips.filter((t) => ['sent', 'draft'].includes((t.status || '').toLowerCase())).length;
    const confirmed = data.trips.filter((t) => ['confirmed', 'accepted'].includes((t.status || '').toLowerCase())).length;
    const closed = data.trips.filter((t) => ['completed', 'closed', 'rejected', 'cancelled'].includes((t.status || '').toLowerCase())).length;
    return [
      { name: 'New',         count: newLeads,   color: 'bg-slate-100 text-slate-700 border-slate-200' },
      { name: 'Contacted',   count: contacted,  color: 'bg-blue-50 text-blue-700 border-blue-200' },
      { name: 'Quote Sent',  count: sent,       color: 'bg-amber-50 text-amber-700 border-amber-200' },
      { name: 'Confirmed',   count: confirmed,  color: 'bg-teal-50 text-teal-700 border-teal-200' },
      { name: 'Closed',      count: closed,     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    ];
  }, [data]);

  // Metric cards
  const metrics = useMemo(() => {
    const openLeads = data.websiteLeads.filter((l) => (l.status || 'new') === 'new');
    const openLeadsThisWeek = openLeads.filter((l) => {
      const d = parseDate(l.createdAt); return d && d >= weekAgo;
    }).length;

    // "Quotes Sent" = trips that have actually been sent to the client.
    const sentTrips = data.trips.filter((t) => (t.status || '').toLowerCase() === 'sent');
    const sentValue = sentTrips.reduce((s, t) => s + Number(t.totalCost || 0), 0);

    // "Quote Pending" = leads in `new`/`contacted` that don't have a sent trip yet
    // (i.e. need a quote prepared).
    const pendingQuoteLeads = data.websiteLeads.filter((l) =>
      ['new', 'contacted'].includes((l.status || 'new').toLowerCase())
    );

    // "Trips This Month" must use CONFIRMED-only pax per spec.
    const tripsThisMonth = data.trips.filter((t) => {
      const d = parseDate(t.createdAt);
      return d && d >= startOfMonth;
    });
    const confirmedThisMonth = tripsThisMonth.filter((t) =>
      ['confirmed', 'accepted', 'completed'].includes((t.status || '').toLowerCase())
    );
    const paxThisMonth = confirmedThisMonth.reduce(
      (s, t) => s + (t.adults || 0) + (t.children || 0) + (t.infants || 0), 0
    );

    const now = new Date();
    const pendingLinks = data.paymentLinks.filter((p) => (p.status || '').toLowerCase() === 'pending');
    const paymentsDue = pendingLinks.reduce((s, p) => s + Number(p.amount || 0), 0);
    const overduePayments = pendingLinks.filter((p) => {
      const exp = parseDate(p.expiresAt); return exp && exp < now;
    }).length;

    // Payments Due — total OUTSTANDING receivables across ALL invoices
    // (NOT payment links). Sum of (totalAmount - amountPaid) for invoices
    // that aren't fully paid / cancelled / converted.
    let invoicesOutstandingTotal = 0;
    let invoicesOutstandingCount = 0;
    for (const inv of data.invoices) {
      const st = (inv.status || '').toLowerCase();
      if (st === 'cancelled' || st === 'converted') continue;
      const total = Number(inv.totalAmount || 0);
      const paid = Number(inv.amountPaid || 0);
      const outstanding = total - paid;
      const isPaid = (inv.paymentStatus || '').toLowerCase() === 'paid' || st === 'paid' || outstanding <= 0;
      if (isPaid) continue;
      invoicesOutstandingTotal += outstanding;
      invoicesOutstandingCount += 1;
    }

    // Phase 16 — counts used by the new PriorityAlerts row. Computed
    // from existing data so we don't issue extra API calls.
    const todayIso = new Date().toISOString().slice(0, 10);
    const voucherTripIds2 = new Set(data.vouchers.map((v) => v.tripId).filter(Boolean));
    const vouchersMissing = data.trips.filter((t) =>
      ['confirmed', 'accepted', 'completed'].includes((t.status || '').toLowerCase()) &&
      !voucherTripIds2.has(t.tripId),
    ).length;
    const overdueInvoices = data.invoices.filter((inv) =>
      !['paid', 'cancelled'].includes((inv.status || '').toLowerCase()) &&
      (inv.dueDate || '').slice(0, 10) &&
      (inv.dueDate || '').slice(0, 10) < todayIso,
    ).length;

    return {
      openLeads, openLeadsThisWeek,
      sentTrips, sentValue,
      pendingQuoteLeads,
      tripsThisMonth: confirmedThisMonth, // confirmed-only count
      paxThisMonth,
      paymentsDue, overduePayments,
      invoicesOutstandingTotal, invoicesOutstandingCount,
      vouchersMissing, overdueInvoices,
    };
  }, [data, weekAgo, startOfMonth]);

  // Follow-ups filtered by selected tab (today / week / month).
  // - "today"  → due today or overdue
  // - "week"   → due within next 7 days (inclusive)
  // - "month"  → due within next 30 days (inclusive)
  const followUpsFiltered = useMemo(() => {
    if (followUpTab === 'today') return followUps.filter((f) => f.daysDiff <= 0);
    if (followUpTab === 'week') return followUps.filter((f) => f.daysDiff <= 7);
    return followUps; // month — already capped at 7 in the source. Recompute below.
  }, [followUps, followUpTab]);

  // The base followUps list filters at ≤ 7 days. For the "Month" sub-tab we
  // need a wider window — recompute on demand for month view only.
  const followUpsMonth = useMemo(() => {
    if (followUpTab !== 'month') return [];
    const list = [];
    const covered = new Set();
    for (const fu of data.crmFollowups) {
      if ((fu.outcome || 'pending') !== 'pending') continue;
      const due = parseDate(fu.scheduled_date);
      if (!due) continue;
      const diff = daysBetween(startOfDay(due), today);
      if (diff > 30) continue;
      let label;
      if (diff < 0) label = 'Overdue';
      else if (diff === 0) label = 'Today';
      else if (diff === 1) label = 'Tomorrow';
      else label = 'Upcoming';
      const isTrip = !!fu.trip_id;
      covered.add(isTrip ? `trip:${fu.trip_obj_id || fu.trip_id}` : `lead:${fu.lead_id}`);
      list.push({
        kind: isTrip ? 'trip' : 'lead', id: fu.id,
        title: fu.entity_name || 'Follow-up', sub: fu.entity_sub || fu.notes || '',
        phone: fu.entity_phone || '', nextDate: due, callCount: 0,
        href: fu.href || (isTrip ? (fu.trip_obj_id ? `/quotes/${fu.trip_obj_id}/edit` : '/quotes') : '/leads'),
        label, daysDiff: diff,
      });
    }
    for (const l of data.websiteLeads) {
      if (!l.nextFollowUpDate) continue;
      if (['converted', 'lost'].includes((l.status || '').toLowerCase())) continue;
      if (covered.has(`lead:${l.leadId}`)) continue;
      const due = parseDate(l.nextFollowUpDate);
      if (!due) continue;
      const diff = daysBetween(startOfDay(due), today);
      if (diff > 30) continue;
      let label;
      if (diff < 0) label = 'Overdue';
      else if (diff === 0) label = 'Today';
      else if (diff === 1) label = 'Tomorrow';
      else label = 'Upcoming';
      list.push({
        kind: 'lead', id: l.leadId, title: l.name || l.email || 'Lead',
        sub: l.destination || l.email || '', phone: l.phone, nextDate: due,
        callCount: l.callCount || 0, href: '/website?tab=leads',
        label, daysDiff: diff,
      });
    }
    return list.sort((a, b) => a.daysDiff - b.daysDiff).slice(0, 20);
  }, [followUpTab, data.websiteLeads, data.crmFollowups, today]);

  const followUpsToShow = followUpTab === 'month' ? followUpsMonth : followUpsFiltered;

  // Follow-ups due today count for header
  const followUpsDueToday = useMemo(
    () => followUps.filter((f) => f.label === 'Today' || f.label === 'Overdue').length,
    [followUps]
  );

  // This-month revenue/expenses — prefer accounting summary, fall back to adminDashboard.
  // Use `revenueExTax` so the headline excludes GST + TCS (which we collect
  // on behalf of the govt — it's not real margin).
  const monthSummary = useMemo(() => {
    const rev = data.accounting?.revenueExTax
      ?? data.accounting?.totalRevenue
      ?? data.adminDashboard?.totals?.monthlyRevenue
      ?? 0;
    const exp = data.accounting?.totalExpenses ?? 0;
    return { revenue: rev, expenses: exp, net: rev - exp };
  }, [data.accounting, data.adminDashboard]);

  // ---- RENDER ----------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-2" data-testid="dashboard-root">
      {/* 0. PACE STRIP (Phase 16) ============================================ */}
      <PaceStrip pace={plus.pace} />

      {/* 1. HEADER BAR ======================================================== */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3" data-testid="dashboard-header">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight">
            {greeting}, {user?.name ? user.name.split(' ')[0] : 'Admin'}
          </h1>
          <p className="text-xs text-slate-600 mt-1 flex items-center gap-2" data-testid="dashboard-date-and-followups">
            <span>{todayLabel}</span>
            <span className="text-slate-300">•</span>
            <span className={`font-medium ${followUpsDueToday > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {followUpsDueToday === 0 ? 'No follow-ups due today' : `${followUpsDueToday} follow-up${followUpsDueToday > 1 ? 's' : ''} due today`}
            </span>
          </p>
        </div>
      </div>

      {/* 2. SMART ASSIST CHIPS (Phase 16.2 — replaces the old PriorityAlerts
            row; chips are dismissible/snoozable for 24h and cover all the
            same urgent items the old red cards displayed). =================== */}
      <SmartAssist chips={plus.assist} onChange={refetchPlus} />

      {/* Recon Auto-Watch — Admin/Accounts flag count → full report. */}
      <ReconWatchChip />
      <TallyExportReminder />


      {/* 3. QUICK ACTIONS (Phase 16) ========================================= */}
      <QuickActions />

      {/* 4. METRIC ROW ======================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="dashboard-metrics">
        <MetricCard
          testId="metric-open-leads"
          title="Open Leads"
          value={data.leadStats?.open ?? metrics.openLeads.length}
          delta={`${metrics.openLeadsThisWeek > 0 ? '+' : ''}${metrics.openLeadsThisWeek} this week`}
          icon={Users}
          tone="teal"
          href="/website?status=open"
        />
        <MetricCard
          testId="metric-quotes-sent"
          title="Quotes Sent"
          value={metrics.sentTrips.length}
          delta={`${INR(metrics.sentValue)} pipeline${metrics.pendingQuoteLeads.length > 0 ? ` · ${metrics.pendingQuoteLeads.length} pending quote` : ''}`}
          icon={FileText}
          tone="amber"
          href="/quotes?status=sent"
        />
        <MetricCard
          testId="metric-trips-this-month"
          title="Trips This Month"
          value={metrics.tripsThisMonth.length}
          delta={`${metrics.paxThisMonth} confirmed pax`}
          icon={Plane}
          tone="emerald"
          href="/quotes?status=confirmed"
        />
        <MetricCard
          testId="metric-payments-due"
          title="Payments Due"
          value={INR(metrics.invoicesOutstandingTotal)}
          delta={`${metrics.invoicesOutstandingCount} invoice${metrics.invoicesOutstandingCount === 1 ? '' : 's'} outstanding`}
          icon={Wallet}
          tone={metrics.invoicesOutstandingCount > 0 ? 'red' : 'slate'}
          href="/finance/invoices?status=outstanding"
        />
      </div>

      {/* 5. THREE-COLUMN CARDS (Phase 17.2 #10 — promoted ABOVE Targets/
            Collections per user spec; was previously below) =============== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        {/* A. Follow-ups due */}
        <SectionCard
          testId="card-followups"
          title="Follow-ups Due"
          action={
            <div className="flex items-center gap-2">
              <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="followup-tabs">
                {[
                  { k: 'today', label: 'Today' },
                  { k: 'week', label: 'Week' },
                  { k: 'month', label: 'Month' },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.k}
                    onClick={() => setFollowUpTab(t.k)}
                    data-testid={`followup-tab-${t.k}`}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded-md transition-colors ${
                      followUpTab === t.k
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <Link to="/quotes" className="text-xs text-teal-700 hover:underline">View all</Link>
            </div>
          }
          isEmpty={followUpsToShow.length === 0}
          emptyHint={<span><span className="font-semibold text-slate-700 block mb-1">No follow-ups due</span>Great work — inbox zero!</span>}
        >
          <ul className="divide-y divide-slate-100 -m-4">
            {followUpsToShow.map((f) => (
              <li key={f.kind + '-' + f.id} data-testid={'followup-' + f.id} className="px-4 py-2.5 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Link to={f.href} className="text-sm font-medium text-slate-900 hover:text-teal-700 block truncate">
                      {f.title}
                      {f.callCount > 0 && (
                        <span className="ml-2 text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          Call #{f.callCount + 1}
                        </span>
                      )}
                    </Link>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{f.sub}</span>
                      {f.phone && (
                        <>
                          <span className="text-slate-300">•</span>
                          <a href={'tel:' + f.phone} className="flex items-center gap-1 hover:text-teal-700">
                            <Phone className="h-3 w-3" />{f.phone}
                          </a>
                        </>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Next: {f.nextDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <StatusPill status={f.label} />
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* B. Upcoming departures */}
        <SectionCard
          testId="card-upcoming-departures"
          title="Upcoming Departures"
          action={
            <div className="flex items-center gap-2">
              <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="departure-tabs">
                {[
                  { k: 'today', label: 'Today' },
                  { k: 'week', label: 'Week' },
                  { k: 'month', label: 'Month' },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.k}
                    onClick={() => setDepartureTab(t.k)}
                    data-testid={`departure-tab-${t.k}`}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded-md transition-colors ${
                      departureTab === t.k
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <Link to="/quotes" className="text-xs text-teal-700 hover:underline">View all</Link>
            </div>
          }
          isEmpty={upcoming.length === 0 && ongoingToday.length === 0}
          emptyHint={<span><span className="font-semibold text-slate-700 block mb-1">No departures in this window</span>Switch the tab above for a wider horizon.</span>}
        >
          {/* Ongoing today — pinned at top when any are in flight */}
          {ongoingToday.length > 0 && (
            <div className="mb-3 pb-2 border-b border-slate-100" data-testid="ongoing-today-block">
              <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Ongoing Today · {ongoingToday.length}
              </div>
              <ul className="space-y-1.5">
                {ongoingToday.map(({ trip, pax }) => (
                  <li key={`ongoing-${trip.id}`} data-testid={`ongoing-${trip.tripId}`} className="flex items-center gap-2 text-xs">
                    <Plane className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                    <Link to={`/quotes/${trip.id}/edit`} className="font-medium text-slate-800 hover:text-emerald-700 truncate flex-1">
                      {trip.destination || '—'}
                    </Link>
                    <DepartureWeather destination={trip.destination} />
                    <span className="text-slate-500">{pax} pax</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {upcoming.length === 0 ? (
            <div className="text-xs text-slate-400 italic px-2 py-2">No departures in this window.</div>
          ) : (
            <ul className="divide-y divide-slate-100 -mx-4">
              {upcoming.map(({ trip, startDate, pax, vouchersMissing, visaPending }) => (
                <li key={trip.id} data-testid={`departure-${trip.tripId}`} className="px-4 py-2.5 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <Link to={`/quotes/${trip.id}/edit`} className="text-sm font-medium text-slate-900 hover:text-teal-700 block truncate">
                        {trip.destination || '—'}
                      </Link>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{pax} pax</span>
                        <span className="text-slate-300">•</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                        <DepartureWeather destination={trip.destination} leadingSep />
                      </p>
                      <div className="mt-1 flex flex-wrap gap-2 items-center">
                        {(() => {
                          const pay = tripPaymentStatus(trip, data.invoices);
                          return pay ? (
                            <span data-testid={`departure-payment-${trip.tripId}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${PAYMENT_BADGE[pay].cls}`}>
                              {PAYMENT_BADGE[pay].label}
                            </span>
                          ) : null;
                        })()}
                        {visaPending && <span className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Visa pending</span>}
                        {vouchersMissing && <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Vouchers missing</span>}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {daysBetween(startOfDay(startDate), today) === 0 ? 'today' : `in ${daysBetween(startOfDay(startDate), today)}d`}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* C. Recent activity feed */}
        <SectionCard
          testId="card-activity"
          title="Recent Activity"
          action={
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="activity-tabs">
              {[
                { k: 'all', label: 'All' },
                { k: 'leads', label: 'Leads' },
                { k: 'trips', label: 'Trips' },
              ].map((t) => (
                <button
                  type="button"
                  key={t.k}
                  onClick={() => setActivityTab(t.k)}
                  data-testid={`activity-tab-${t.k}`}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded-md transition-colors ${
                    activityTab === t.k
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          }
          isEmpty={activity.length === 0}
          emptyHint={<span><Activity className="h-5 w-5 mx-auto text-slate-300 mb-2" /><span className="block font-semibold text-slate-700">No recent activity</span>Events will appear as you use the CRM.</span>}
        >
          <ul className="space-y-1.5">
            {activity.map((e, i) => {
              const Icon = e.icon || Activity;
              return (
                <li key={i} data-testid={`activity-${i}`} className="flex items-start gap-2">
                  <div className="shrink-0 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center mt-0.5">
                    <Icon className="h-3 w-3 text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={e.href || '#'} className="text-xs text-slate-700 hover:text-teal-700 block line-clamp-1">
                      {e.text}
                    </Link>
                    <p className="text-[10px] text-slate-400">{relativeTime(e.when)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      </div>

      {/* 6. TARGETS (60%) + TOP DESTINATIONS (40%) — side by side per Phase
            16.2 spec. Both share the same row height. Phase 17.2 #10 moved
            BELOW the 3-column cards so daily ops (follow-ups / departures /
            activity) sit above pipeline reporting. =========================== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-2">
        <div className="lg:col-span-3">
          <TargetsWidget />
        </div>
        <div className="lg:col-span-2">
          <TopDestinationsCard items={topDestinations} period={destPeriod} onPeriodChange={setDestPeriod} />
        </div>
      </div>

      {/* 7. COLLECTIONS ===================================================== */}
      <CollectionsWidget />

      {/* 8. VISA PENDING + CLIENT MOMENTS (same row — Phase: compact layout).
            Lead Health card removed (DEDUP); its "cold leads" signal is merged
            into Pipeline Snapshot below. ==================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-start">
        <VisaPendingWidget />
        <ClientMoments moments={plus.moments} />
      </div>

      {/* 3.5 TOP DESTINATIONS (now lives in the Targets+TopDest grid row
            above — keeping this slot intentionally empty so layout doesn't
            shift on legacy bookmarks). =========================================== */}

      {/* 4. BOTTOM ROW ======================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* A. Pipeline snapshot */}
        <SectionCard
          testId="card-pipeline"
          title="Pipeline Snapshot"
          action={<Link to="/quotes" className="text-xs text-teal-700 hover:underline">View pipeline</Link>}
        >
          <div className="grid grid-cols-5 gap-2">
            {pipeline.map((stage) => (
              <div key={stage.name} data-testid={`pipeline-${stage.name.toLowerCase().replace(/\s+/g, '-')}`} className={`rounded-lg border p-3 text-center ${stage.color}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{stage.name}</div>
                <div className="text-2xl font-heading font-bold mt-1">{stage.count}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            Total deals in pipeline:
            <span className="font-semibold text-slate-800">{pipeline.reduce((s, p) => s + p.count, 0)}</span>
          </p>
          {/* Lead Health merged in (DEDUP) — cold = new/contacted leads with
              no update in > 7 days, the "slipping through the cracks" signal. */}
          {(plus.leadHealth?.cold || 0) > 0 ? (
            <Link
              to="/leads?filter=cold"
              data-testid="pipeline-at-risk"
              className="mt-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 hover:bg-amber-100 transition-colors"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>At risk: <span className="font-semibold">{plus.leadHealth.cold}</span> lead{plus.leadHealth.cold === 1 ? '' : 's'} cold &gt; 7 days</span>
            </Link>
          ) : (
            <div data-testid="pipeline-at-risk" className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              No leads at risk — all followed up within 7 days
            </div>
          )}
        </SectionCard>

        {/* B. This month summary */}
        <SectionCard
          testId="card-month-summary"
          title="This Month"
          action={<Link to="/accounting" className="text-xs text-teal-700 hover:underline">View accounting</Link>}
        >
          <div className="grid grid-cols-3 gap-3">
            <div data-testid="summary-revenue" className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Revenue · Net of Tax</div>
              <div className="text-xl font-heading font-bold text-emerald-900 mt-1 truncate">{INR(monthSummary.revenue)}</div>
            </div>
            <div data-testid="summary-expenses" className="rounded-lg bg-amber-50 border border-amber-100 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Expenses</div>
              <div className="text-xl font-heading font-bold text-amber-900 mt-1 truncate">{INR(monthSummary.expenses)}</div>
            </div>
            <div data-testid="summary-net" className={`rounded-lg border p-3 ${monthSummary.net >= 0 ? 'bg-teal-50 border-teal-100' : 'bg-red-50 border-red-100'}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wider ${monthSummary.net >= 0 ? 'text-teal-700' : 'text-red-700'}`}>Net Margin</div>
              <div className={`text-xl font-heading font-bold mt-1 truncate ${monthSummary.net >= 0 ? 'text-teal-900' : 'text-red-900'}`}>{INR(monthSummary.net)}</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
            <FileCheck className="h-3.5 w-3.5" />
            {data.invoices.length} invoice{data.invoices.length !== 1 && 's'} issued to date
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default Dashboard;
